import { toast } from "sonner";
import type { InformacoesClinica } from "@/hooks/useInformacoesClinica";
import { formatLocalDateBR } from "@/lib/date-utils";
import { formatClinicAddress } from "@/lib/clinic-information";

type ClinicaPrint = (Partial<InformacoesClinica> & {
  nome?: string;
  razao_social?: string;
  nome_fantasia?: string;
}) | null | undefined;

interface ReceitaPrintData {
  pacienteNome?: string;
  data?: string;
  medicamentosHtml: string;
  observacoes?: string;
  profissional?: string;
  cro?: string;
  clinica?: ClinicaPrint;
}

interface AtestadoPrintData {
  titulo: string;
  conteudo: string;
  pacienteNome?: string;
  profissional?: string;
  cro?: string;
  clinica?: ClinicaPrint;
}

interface ReciboPrintData {
  pacienteNome?: string;
  pacienteCpf?: string;
  data?: string;
  servico: string;
  valor: number;
  formaPagamento?: string;
  observacoes?: string;
  profissional?: string;
  cro?: string;
  clinica?: ClinicaPrint;
}

interface ReciboPagamentoFuncionarioPrintData {
  funcionarioNome: string;
  cargo?: string;
  competenciaInicio: string;
  competenciaFim: string;
  dataPagamento?: string;
  formaPagamento?: string;
  salarioBase: number;
  producao: number;
  percentualComissao: number;
  valorComissao: number;
  bonusMeta: number;
  descontos: number;
  valorTotal: number;
  observacoes?: string;
  clinica?: ClinicaPrint;
}

function escapeHtml(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function dateBr(value?: string) {
  return formatLocalDateBR(value, "__/__/____");
}

function contactLine(clinica: ClinicaPrint) {
  const telefones = [clinica?.telefone, clinica?.celular].filter(Boolean).join(" / ");
  return telefones || "Telefone: ______________________________";
}

function addressLine(clinica: ClinicaPrint) {
  const endereco = formatClinicAddress(clinica);
  return endereco || "Endereco: ______________________________________________";
}

function clinicName(clinica: ClinicaPrint) {
  return clinica?.razao_social || clinica?.nome_clinica || clinica?.nome_fantasia || clinica?.nome || "Clínica Odontológica";
}

function brandHtml(clinica: ClinicaPrint) {
  const nome = clinicName(clinica);
  const cnpj = clinica?.cnpj ? `CNPJ: ${clinica.cnpj}` : "";
  const logo = clinica?.logo_base64
    ? `<img src="${clinica.logo_base64}" alt="${escapeHtml(nome)}" class="logo-img" />`
    : `<div class="logo-mark"><span></span><span></span><span></span><span></span></div>`;

  return `
    <div class="brand">
      <div class="brand-main">${logo}<div><div class="brand-name">${escapeHtml(nome)}</div><div class="tagline">Cuidando do seu sorriso</div></div></div>
      ${cnpj ? `<div class="cnpj">${escapeHtml(cnpj)}</div>` : ""}
    </div>
  `;
}

export function cloudOdontoSignatureHtml() {
  return `<div class="platform-signature" style="display:flex;align-items:center;justify-content:flex-end;gap:5px;margin-top:12px;color:#475569;font:9px Arial,sans-serif"><span>Gerado por</span><img src="/cloud-odonto-icon.png" alt="" style="width:17px;height:17px;object-fit:contain" /><strong style="color:#0f5fbb;font-size:10px">Cloud Odonto</strong></div>`;
}

export function clinicBrandHeaderHtml(clinica: ClinicaPrint) {
  const nome = clinicName(clinica);
  const endereco = formatClinicAddress(clinica);
  const contato = [clinica?.telefone, clinica?.celular, clinica?.email].filter(Boolean).join(" | ");
  const logo = clinica?.logo_base64 ? `<img src="${escapeHtml(clinica.logo_base64)}" alt="${escapeHtml(nome)}" style="width:64px;height:64px;object-fit:contain" />` : "";
  return `<header style="display:flex;align-items:center;gap:14px;border-bottom:1px solid #cbd5e1;padding-bottom:12px;margin-bottom:20px">${logo}<div><div style="font:700 22px Arial,sans-serif;color:#111827">${escapeHtml(nome)}</div>${clinica?.cnpj ? `<div style="font:12px Arial,sans-serif;color:#475569">CNPJ: ${escapeHtml(clinica.cnpj)}</div>` : ""}${endereco ? `<div style="font:12px Arial,sans-serif;color:#475569">${escapeHtml(endereco)}</div>` : ""}${contato ? `<div style="font:12px Arial,sans-serif;color:#475569">${escapeHtml(contato)}</div>` : ""}</div></header>`;
}

function footerHtml(clinica: ClinicaPrint) {
  return `
    <footer class="footer">
      <div class="contact">☎ ${escapeHtml(contactLine(clinica))}</div>
      <div class="contact">⌖ ${escapeHtml(addressLine(clinica))}</div>
      <div class="contact">✉ ${escapeHtml(clinica?.email || "Email: ______________________________")}</div>
      ${clinica?.observacoes ? `<div class="units">${escapeHtml(clinica.observacoes).replace(/\n/g, "<br />")}</div>` : ""}
      ${cloudOdontoSignatureHtml()}
    </footer>
  `;
}

function baseCss() {
  return `
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    body { margin: 0; color: #111; font-family: Arial, Helvetica, sans-serif; background: #fff; }
    .sheet { min-height: 270mm; border: 1px solid #222; padding: 18mm 12mm 10mm; position: relative; }
    .brand { text-align: center; margin-bottom: 20px; }
    .brand-main { display: inline-flex; align-items: center; gap: 10px; }
    .brand-name { font-size: 34px; font-weight: 700; line-height: 1; }
    .tagline { margin-top: 2px; font-size: 12px; font-style: italic; }
    .cnpj { margin-top: 8px; font-size: 14px; }
    .logo-img { width: 56px; height: 56px; object-fit: contain; }
    .logo-mark { width: 54px; height: 54px; border-radius: 50%; display: grid; grid-template-columns: 1fr 1fr; gap: 4px; overflow: hidden; }
    .logo-mark span { background: #111; display: block; }
    .doc-title { text-align: center; font-size: 27px; text-decoration: underline; margin: 20px 0 28px; font-weight: 400; }
    .line { border-bottom: 1px solid #222; display: inline-block; min-width: 180px; height: 1.1em; vertical-align: baseline; }
    .footer { position: absolute; left: 12mm; right: 12mm; bottom: 9mm; font-size: 18px; line-height: 1.45; }
    .contact { margin: 3px 0; }
    .units { margin-top: 22px; text-align: center; font-size: 15px; line-height: 1.3; }
    .platform-signature { display: flex; align-items: center; justify-content: flex-end; gap: 5px; margin-top: 10px; color: #475569; font-size: 9px; }
    .platform-signature img { width: 17px; height: 17px; object-fit: contain; }
    .platform-signature strong { color: #0f5fbb; font-size: 10px; }
    .signatures { display: flex; justify-content: space-between; gap: 48px; margin-top: 70px; font-size: 13px; text-align: center; }
    .signature-line { border-top: 1px solid #222; min-width: 210px; padding-top: 6px; }
    .no-print { margin-top: 20px; text-align: center; }
    .no-print button { border: 0; background: #111827; color: #fff; padding: 10px 16px; border-radius: 4px; cursor: pointer; }
    @media print { .no-print { display: none; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;
}

function openPrint(title: string, body: string) {
  const popup = window.open("", "_blank", "width=840,height=940");
  if (!popup) {
    toast.error("O navegador bloqueou a janela de impressao.");
    return;
  }
  popup.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>${baseCss()}</style>
      </head>
      <body>${body}<script>setTimeout(function(){ window.print(); }, 250);</script></body>
    </html>
  `);
  popup.document.close();
}

function moneyBr(value: number) {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function numeroPorExtensoAte999(value: number): string {
  const unidades = ["", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"];
  const especiais = ["dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
  const dezenas = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
  const centenas = ["", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];

  if (value === 0) return "zero";
  if (value === 100) return "cem";
  const parts: string[] = [];
  const centena = Math.floor(value / 100);
  const resto = value % 100;
  if (centena) parts.push(centenas[centena]);
  if (resto >= 20) {
    const dezena = Math.floor(resto / 10);
    const unidade = resto % 10;
    parts.push(dezenas[dezena]);
    if (unidade) parts.push(unidades[unidade]);
  } else if (resto >= 10) {
    parts.push(especiais[resto - 10]);
  } else if (resto > 0) {
    parts.push(unidades[resto]);
  }
  return parts.join(" e ");
}

function inteiroPorExtenso(value: number): string {
  if (value === 0) return "zero";
  if (value > 999999) return String(value);
  const milhares = Math.floor(value / 1000);
  const resto = value % 1000;
  const parts: string[] = [];
  if (milhares) {
    parts.push(milhares === 1 ? "mil" : `${numeroPorExtensoAte999(milhares)} mil`);
  }
  if (resto) parts.push(numeroPorExtensoAte999(resto));
  return parts.join(resto < 100 && milhares ? " e " : " ");
}

function valorPorExtenso(value: number) {
  const totalCentavos = Math.round(Number(value || 0) * 100);
  const reais = Math.floor(totalCentavos / 100);
  const centavos = totalCentavos % 100;
  const partes: string[] = [];
  if (reais) partes.push(`${inteiroPorExtenso(reais)} ${reais === 1 ? "real" : "reais"}`);
  if (centavos) partes.push(`${inteiroPorExtenso(centavos)} ${centavos === 1 ? "centavo" : "centavos"}`);
  return partes.length ? partes.join(" e ") : "zero real";
}

export function printReceituarioModelo(data: ReceitaPrintData) {
  const body = `
    <main class="sheet prescription">
      ${brandHtml(data.clinica)}
      <h1 class="doc-title">Receituário</h1>
      <div class="patient-row">Paciente: <span class="line">${escapeHtml(data.pacienteNome || "")}</span></div>
      <section class="prescription-content">
        ${data.medicamentosHtml}
        ${data.observacoes ? `<div class="obs">${escapeHtml(data.observacoes).replace(/\n/g, "<br />")}</div>` : ""}
      </section>
      <section class="professional-sign">
    <div class="stamp">${escapeHtml(data.profissional || "Responsável técnico")}<br />${data.cro ? `CRO ${escapeHtml(data.cro)}` : ""}</div>
      </section>
      ${footerHtml(data.clinica)}
    </main>
    <style>
      .prescription .patient-row { font-size: 22px; margin: 26px 0 60px; }
      .prescription .patient-row .line { min-width: 76%; }
      .prescription-content { min-height: 82mm; padding: 8px 0; font-size: 24px; line-height: 1.55; }
      .rx-item { margin: 0 0 18px; white-space: pre-wrap; }
      .rx-item strong { font-size: 24px; }
      .rx-meta { font-size: 21px; }
      .obs { margin-top: 18px; font-size: 20px; }
      .professional-sign { height: 46mm; position: relative; }
      .stamp { position: absolute; right: 26mm; bottom: 8mm; text-align: center; font-size: 16px; font-weight: 700; }
    </style>
  `;
  openPrint(`Receituario - ${data.pacienteNome || ""}`, body);
}

export function printAtestadoModelo(data: AtestadoPrintData) {
  const body = `
    <main class="sheet certificate">
      ${brandHtml(data.clinica)}
      <h1 class="doc-title">Atestado Odontológico</h1>
      <section class="certificate-text">${escapeHtml(data.conteudo).replace(/\n/g, "<br />")}</section>
      <div class="city-date">${escapeHtml(data.clinica?.cidade || "Manaus")} ____/____/______</div>
      <div class="signatures">
        <div class="signature-line">Assinatura paciente</div>
        <div class="signature-line">Carimbo/ Assinatura</div>
      </div>
      ${footerHtml(data.clinica)}
    </main>
    <style>
      .certificate .doc-title { margin-bottom: 30px; }
      .certificate-text { min-height: 98mm; font-size: 22px; line-height: 2.1; white-space: normal; }
      .certificate-text .blank { border-bottom: 1px solid #222; display: inline-block; min-width: 150px; }
      .city-date { margin-top: 28px; font-size: 20px; }
    </style>
  `;
  openPrint(data.titulo || "Atestado Odontológico", body);
}

export function printReciboModelo(data: ReciboPrintData) {
  const clinicNameValue = clinicName(data.clinica);
  const cnpj = data.clinica?.cnpj || "____________________________";
  const endereco = addressLine(data.clinica);
  const contatos = [contactLine(data.clinica), data.clinica?.email].filter(Boolean).join(" | ");
  const body = `
    <main class="sheet receipt-doc">
      <header class="receipt-header">
        ${brandHtml(data.clinica)}
        <div class="clinic-line">${escapeHtml(clinicNameValue)}</div>
        <div class="clinic-contact">${escapeHtml(contatos || "Contato: ______________________________")}</div>
        <div class="clinic-address">${escapeHtml(endereco)}</div>
      </header>

      <h1 class="receipt-title">RECIBO</h1>

      <section class="receipt-fields">
        <div class="top-row">
          <div class="date-field"><span>DATA:</span> ${escapeHtml(dateBr(data.data))}</div>
          <div class="value-field">${escapeHtml(moneyBr(data.valor))}</div>
        </div>

        <div class="field-block">
          <span>NOME</span>
          <strong>${escapeHtml(data.pacienteNome || "")}</strong>
          ${data.pacienteCpf ? `<small>CPF: ${escapeHtml(data.pacienteCpf)}</small>` : ""}
        </div>

        <div class="field-block">
          <span>VALOR POR EXTENSO:</span>
          <strong>(${escapeHtml(valorPorExtenso(data.valor).toUpperCase())})</strong>
        </div>

        <div class="field-block">
          <span>DESCRIÇÃO:</span>
          <strong>${escapeHtml(data.servico)}</strong>
        </div>

        <div class="field-block">
          <span>Forma de Pagamento:</span>
          <strong>${escapeHtml((data.formaPagamento || "-").toUpperCase())}</strong>
        </div>

        ${data.observacoes ? `<div class="field-block notes"><span>OBSERVAÇÕES:</span>${escapeHtml(data.observacoes).replace(/\n/g, "<br />")}</div>` : ""}

        <div class="signature-row">
          <div>
            <span>CNPJ:</span>
            <strong>${escapeHtml(cnpj)}</strong>
          </div>
          <div class="signature-line">Assinatura / Carimbo</div>
        </div>
      </section>

      ${footerHtml(data.clinica)}
    </main>
    <style>
      .receipt-doc { padding-top: 12mm; }
      .receipt-header { text-align: center; border-bottom: 2px solid #111; padding-bottom: 12px; margin-bottom: 22px; }
      .receipt-header .brand { margin-bottom: 10px; }
      .receipt-header .brand-name { font-size: 32px; }
      .receipt-header .logo-img { width: 78px; height: 54px; }
      .receipt-header .logo-mark { width: 52px; height: 52px; }
      .clinic-line { font-size: 16px; font-weight: 700; text-transform: uppercase; margin-top: 4px; }
      .clinic-contact, .clinic-address { font-size: 13px; line-height: 1.35; }
      .receipt-title { text-align: center; font-size: 34px; letter-spacing: .08em; margin: 14px 0 24px; font-weight: 800; }
      .receipt-fields { min-height: 132mm; font-size: 18px; }
      .top-row { display: grid; grid-template-columns: 1fr 190px; gap: 22px; align-items: center; margin-bottom: 24px; }
      .date-field span { font-weight: 700; }
      .value-field { border: 2px solid #111; border-radius: 2px; padding: 10px 14px; text-align: center; font-size: 26px; font-weight: 800; }
      .field-block { border-bottom: 1px solid #111; padding: 8px 0 12px; margin-bottom: 14px; }
      .field-block span { display: block; font-size: 13px; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
      .field-block strong { display: block; font-size: 22px; line-height: 1.35; }
      .field-block small { display: block; margin-top: 2px; font-size: 13px; }
      .notes { min-height: 34px; font-size: 16px; line-height: 1.4; }
      .signature-row { display: grid; grid-template-columns: 1fr 260px; gap: 36px; align-items: end; margin-top: 34px; }
      .signature-row span { display: block; font-size: 13px; font-weight: 700; }
      .signature-row strong { display: block; font-size: 20px; margin-top: 4px; }
      .signature-line { border-top: 1px solid #111; padding-top: 8px; text-align: center; font-size: 13px; }
    </style>
  `;
  openPrint(`Recibo - ${data.pacienteNome || ""}`, body);
}

export function printReciboPagamentoFuncionario(data: ReciboPagamentoFuncionarioPrintData) {
  const clinicNameValue = clinicName(data.clinica);
  const cnpj = data.clinica?.cnpj || "____________________________";
  const endereco = addressLine(data.clinica);
  const contatos = [contactLine(data.clinica), data.clinica?.email].filter(Boolean).join(" | ");
  const body = `
    <main class="sheet receipt-doc employee-payment">
      <header class="receipt-header">
        ${brandHtml(data.clinica)}
        <div class="clinic-line">${escapeHtml(clinicNameValue)}</div>
        <div class="clinic-contact">${escapeHtml(contatos || "Contato: ______________________________")}</div>
        <div class="clinic-address">${escapeHtml(endereco)}</div>
        <div class="clinic-contact">CNPJ: ${escapeHtml(cnpj)}</div>
      </header>

      <h1 class="receipt-title">RECIBO DE PAGAMENTO</h1>

      <section class="receipt-fields">
        <p class="payment-text">
          Eu, <strong>${escapeHtml(data.funcionarioNome)}</strong>${data.cargo ? `, ${escapeHtml(data.cargo)}` : ""},
          declaro ter recebido de <strong>${escapeHtml(clinicNameValue)}</strong> a importância de
          <strong>${escapeHtml(moneyBr(data.valorTotal))}</strong>, referente ao pagamento do período de
          <strong>${escapeHtml(dateBr(data.competenciaInicio))}</strong> a <strong>${escapeHtml(dateBr(data.competenciaFim))}</strong>.
        </p>

        <div class="receipt-grid-lines">
          <div><span>Salário base</span><strong>${escapeHtml(moneyBr(data.salarioBase))}</strong></div>
          <div><span>Produção informada</span><strong>${escapeHtml(moneyBr(data.producao))}</strong></div>
          <div><span>Comissão</span><strong>${escapeHtml(moneyBr(data.valorComissao))} (${escapeHtml(String(data.percentualComissao || 0))}%)</strong></div>
          <div><span>Bônus de meta</span><strong>${escapeHtml(moneyBr(data.bonusMeta))}</strong></div>
          <div><span>Descontos</span><strong>${escapeHtml(moneyBr(data.descontos))}</strong></div>
          <div><span>Total pago</span><strong>${escapeHtml(moneyBr(data.valorTotal))}</strong></div>
        </div>

        <div class="field-block">
          <span>Forma de Pagamento:</span>
          <strong>${escapeHtml((data.formaPagamento || "-").toUpperCase())}</strong>
        </div>

        <div class="field-block">
          <span>DATA DO PAGAMENTO:</span>
          <strong>${escapeHtml(dateBr(data.dataPagamento))}</strong>
        </div>

        ${data.observacoes ? `<div class="field-block notes"><span>OBSERVAÇÕES:</span>${escapeHtml(data.observacoes).replace(/\n/g, "<br />")}</div>` : ""}

        <div class="signature-row">
          <div>
            <span>VALOR POR EXTENSO:</span>
            <strong>(${escapeHtml(valorPorExtenso(data.valorTotal).toUpperCase())})</strong>
          </div>
          <div class="signature-line">Assinatura do funcionário</div>
        </div>
      </section>

      ${footerHtml(data.clinica)}
    </main>
    <style>
      .employee-payment .payment-text { font-size: 20px; line-height: 1.8; margin: 0 0 22px; }
      .receipt-grid-lines { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 18px; }
      .receipt-grid-lines div { border: 1px solid #111; padding: 9px 10px; }
      .receipt-grid-lines span { display: block; font-size: 12px; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
      .receipt-grid-lines strong { font-size: 18px; }
    </style>
  `;
  openPrint(`Recibo de pagamento - ${data.funcionarioNome || ""}`, body);
}

export { escapeHtml, dateBr };
